import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PageHeader from "../PageHeader";
import { domainsApi } from "../../services/api";

// Extension groups the typed term is expanded across. The first is the default.
// Easy to retune — these are just curated TLD buckets.
const TLD_CATEGORIES = [
  {
    key: "popular",
    label: "Popular",
    tlds: ["com", "net", "org", "co", "io", "xyz"],
  },
  {
    key: "featured",
    label: "Featured",
    tlds: ["ai", "app", "dev", "io", "tech", "design"],
  },
  {
    key: "business",
    label: "Business",
    tlds: ["co", "company", "agency", "ltd", "inc", "biz"],
  },
  {
    key: "fun",
    label: "Fun",
    tlds: ["fun", "cool", "lol", "rocks", "club", "live"],
  },
  {
    key: "informational",
    label: "Informational",
    tlds: ["info", "news", "blog", "media", "online", "site"],
  },
];

// LLM name ideas are checked across these; only *available* combos are shown.
// Kept small so labels × TLDs stays under the /check batch cap.
const SUGGEST_TLDS = ["com", "io", "co", "app"];
const MAX_SUGGEST_LABELS = 12; // 12 × 4 = 48 ≤ the 50-name batch cap
const MAX_SUGGESTIONS = 12;
const DEBOUNCE_MS = 300;

// A single label or sld.tld — letters/digits/hyphens (+ dots). Anything else
// (a phrase, spaces, punctuation) isn't a domain name: it only powers the AI
// suggestions, so we skip the exact-availability lookup for it.
const SEARCHABLE_RE = /^[a-z0-9-]+(\.[a-z0-9-]+)*$/;

// "thingboi" -> ["thingboi.com", ...] across `tlds`. An explicit `foo.io` is
// checked as-is. Whitespace-only -> nothing.
function buildCandidates(term, tlds) {
  const cleaned = term.trim().toLowerCase();
  if (!cleaned) return [];
  if (cleaned.includes(".")) return [cleaned];
  return tlds.map((tld) => `${cleaned}.${tld}`);
}

// Turn raw input into searchable SLDs. Domains can't contain spaces, so a
// spaced phrase becomes its two common multi-word forms — hyphenated and
// joined ("blue fin" -> "blue-fin", "bluefin"). Returns [] if, even after
// that, it still isn't a valid name (e.g. other punctuation).
function searchableForms(rawTerm) {
  const base = rawTerm.trim().toLowerCase();
  if (!base) return [];
  const forms = [
    ...new Set([base.replace(/\s+/g, "-"), base.replace(/\s+/g, "")]),
  ];
  return forms.filter((f) => SEARCHABLE_RE.test(f));
}

// The category a picked TLD is checked under: the current one when it
// already has that TLD, else the first that does, else null (no category
// carries it — check the exact name instead).
function categoryForTld(tld, currentKey) {
  const current = TLD_CATEGORIES.find((c) => c.key === currentKey);
  if (current?.tlds.includes(tld)) return current.key;
  return TLD_CATEGORIES.find((c) => c.tlds.includes(tld))?.key ?? null;
}

function formatPrice(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function ResultRow({ result, onPick, picked = false }) {
  const { name, available, premium, price, reason } = result;
  const clickable = typeof onPick === "function";

  let status;
  if (available === true && price != null) {
    status = (
      <span className="flex items-center gap-2">
        <Badge variant="success">Available</Badge>
        {premium && (
          <>
            {" "}
            <Badge variant="warning">Premium</Badge>
          </>
        )}{" "}
        <strong>{formatPrice(price)}</strong>
      </span>
    );
  } else if (reason === "over_cap") {
    status = <Badge variant="secondary">Contact us</Badge>;
  } else if (available === false) {
    status = <Badge variant="secondary">Taken</Badge>;
  } else {
    status = <Badge variant="outline">Unavailable</Badge>;
  }

  const rowContent = (
    <>
      <span className="font-mono">
        {name}
        {picked && (
          <>
            {" "}
            <Badge variant="outline" className="font-sans font-normal">
              You picked this
            </Badge>
          </>
        )}
      </span>
      {/* A real space (free in this flex row): screen readers and agents
          read "monoxplore.com Available", not "monoxplore.comAvailable". */}{" "}
      {status}
    </>
  );

  if (clickable) {
    return (
      <li>
        <button
          type="button"
          onClick={() => onPick(name)}
          className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-left hover:bg-accent"
        >
          {rowContent}
        </button>
      </li>
    );
  }

  return (
    <li
      className={`flex items-center justify-between gap-2 px-4 py-3${picked ? " bg-muted" : ""}`}
    >
      {rowContent}
    </li>
  );
}

ResultRow.propTypes = {
  result: PropTypes.shape({
    name: PropTypes.string.isRequired,
    available: PropTypes.bool, // may be null = unknown
    premium: PropTypes.bool,
    price: PropTypes.number, // cents, may be null
    reason: PropTypes.string,
  }).isRequired,
  // When set, the row is clickable and calls onPick(name).
  onPick: PropTypes.func,
  // The AI idea the user clicked to get here (board #80).
  picked: PropTypes.bool,
};

// Two distinct searches, one at a time:
//  - "availability": is this exact name free across the chosen extensions?
//  - "creative":     AI-suggested brandable names (available ones).
const MODE_AVAILABILITY = "availability";
const MODE_CREATIVE = "creative";

function DomainSearch() {
  const [mode, setMode] = useState(MODE_AVAILABILITY);
  const [term, setTerm] = useState("");
  const [category, setCategory] = useState(TLD_CATEGORIES[0].key);
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [error, setError] = useState(null);
  // The AI idea that was clicked, marked in the availability results.
  const [picked, setPicked] = useState(null);
  const inputRef = useRef(null);
  const mainReqId = useRef(0);
  const suggestReqId = useRef(0);

  const isCreative = mode === MODE_CREATIVE;

  // Availability mode: the exact name(s) across the selected TLD category.
  useEffect(() => {
    if (mode !== MODE_AVAILABILITY) {
      setResults([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const forms = searchableForms(term);
    if (forms.length === 0) {
      setResults([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const active =
      TLD_CATEGORIES.find((c) => c.key === category) || TLD_CATEGORIES[0];
    const candidates = [
      ...new Set(forms.flatMap((f) => buildCandidates(f, active.tlds))),
    ];

    const id = ++mainReqId.current;
    setLoading(true);
    setError(null);

    const timer = setTimeout(() => {
      domainsApi
        .check(candidates)
        .then((res) => {
          if (id === mainReqId.current) setResults(res.data.results || []);
        })
        .catch((err) => {
          if (id !== mainReqId.current) return;
          setResults([]);
          setError(
            err?.response?.status === 400
              ? null
              : "Search is temporarily unavailable. Try again in a moment.",
          );
        })
        .finally(() => {
          if (id === mainReqId.current) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, category, mode]);

  // Creative mode: AI name ideas, availability-checked, available-only,
  // cheapest first.
  useEffect(() => {
    if (mode !== MODE_CREATIVE) {
      setSuggestions([]);
      setSuggestLoading(false);
      return undefined;
    }
    const seed = term.trim().toLowerCase();
    if (!seed) {
      setSuggestions([]);
      setSuggestLoading(false);
      return undefined;
    }

    const id = ++suggestReqId.current;
    setSuggestLoading(true);

    const timer = setTimeout(() => {
      const current = () => id === suggestReqId.current;
      domainsApi
        .suggest(seed)
        .then((res) => {
          if (!current()) return null;
          const labels = (res.data.suggestions || []).slice(
            0,
            MAX_SUGGEST_LABELS,
          );
          if (labels.length === 0) {
            setSuggestions([]);
            return null;
          }
          const names = labels.flatMap((label) =>
            SUGGEST_TLDS.map((tld) => `${label}.${tld}`),
          );
          return domainsApi.check(names);
        })
        .then((res) => {
          if (!res || !current()) return;
          const available = (res.data.results || [])
            .filter((r) => r.available === true)
            .sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
          setSuggestions(available.slice(0, MAX_SUGGESTIONS));
        })
        .catch(() => {
          if (current()) setSuggestions([]);
        })
        .finally(() => {
          if (current()) setSuggestLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, mode]);

  // Click an AI idea -> jump to availability for that name, under an
  // extension category that includes its TLD — keeping "Fun" selected
  // would check monoxplore.fun and leave out the .com that was clicked
  // (board #80).
  const handlePick = (name) => {
    const dot = name.indexOf(".");
    const stem = name.slice(0, dot);
    const key = categoryForTld(name.slice(dot + 1), category);
    if (key) {
      setTerm(stem);
      setCategory(key);
    } else {
      setTerm(name); // no category has this TLD: check it exactly
    }
    setPicked(name);
    setMode(MODE_AVAILABILITY);
    // The clicked row is gone with the mode; don't leave focus on <body>.
    inputRef.current?.focus();
  };

  const trimmed = term.trim();
  const hasTerm = trimmed.length > 0;
  const isExplicit = trimmed.includes(".");
  const hadSpaces = /\s/.test(trimmed);
  const forms = searchableForms(term); // what we actually search (cheap)
  const isUnsearchable = hasTerm && forms.length === 0;
  const activeCategory =
    TLD_CATEGORIES.find((c) => c.key === category) || TLD_CATEGORIES[0];
  const showSpinner = isCreative ? suggestLoading : loading;

  return (
    <div className="page-container mt-6 pt-4">
      <PageHeader
        title="Domain Search"
        subtitle="Check if a name is available, or get AI-suggested names."
      />

      {/* One search at a time — pick the mode explicitly. */}
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={mode}
        onValueChange={(v) => v && setMode(v)}
        aria-label="Search mode"
        className="mb-4"
      >
        <ToggleGroupItem value={MODE_AVAILABILITY}>
          Check availability
        </ToggleGroupItem>
        <ToggleGroupItem value={MODE_CREATIVE}>AI ideas</ToggleGroupItem>
      </ToggleGroup>

      {/* Input + guidance, both mode-specific so it's clear what to type. */}
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="flex">
          <Input
            type="text"
            placeholder={
              isCreative
                ? 'Describe your idea, e.g. "blue fin sushi"'
                : 'Type a name, e.g. "rockethub"'
            }
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              setPicked(null);
            }}
            ref={inputRef}
            aria-label="Domain search"
            autoFocus
            className={showSpinner ? "rounded-r-none" : undefined}
          />
          {showSpinner && (
            <div
              role="status"
              className="flex items-center rounded-r-md border border-l-0 border-input bg-transparent px-3"
            >
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
              <span className="sr-only">Searching…</span>
            </div>
          )}
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          {isCreative
            ? "Our AI suggests brandable, available names — a word or a phrase both work."
            : "We check that exact name across the extensions you pick below."}
        </p>
      </form>

      {/* Extensions only apply to the availability search. */}
      {!isCreative && (
        <div className="mb-4">
          <div className="mb-1 text-sm text-muted-foreground">Extensions</div>
          {/* A single-select filter, not tabs (there is no panel per
              category): role=radiogroup, arrows move focus without firing
              a search, a re-click can't deselect. #34 review B. */}
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={category}
            onValueChange={(v) => v && setCategory(v)}
            aria-label="Extension category"
            className="flex-wrap justify-start gap-1"
          >
            {TLD_CATEGORIES.map((c) => (
              <ToggleGroupItem
                key={c.key}
                value={c.key}
                className="min-h-11 flex-none md:min-h-0"
              >
                {c.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      )}

      {!isCreative && error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ---------- Availability mode ---------- */}
      {!isCreative && (
        <>
          {!hasTerm && !loading && (
            <div className="rounded-lg border bg-card p-4 text-muted-foreground shadow-xs">
              Type a name above to see if it’s free across these extensions.
            </div>
          )}

          {isUnsearchable && (
            <div className="rounded-lg border bg-card p-4 text-muted-foreground shadow-xs">
              ‘{trimmed}’ isn’t a domain name — stick to letters, numbers,
              spaces, and hyphens.
            </div>
          )}

          {results.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                Availability
                <span className="text-base font-normal text-muted-foreground">
                  {forms.map((f) => `‘${f}’`).join(" and ")}
                  {!isExplicit && ` · ${activeCategory.label} extensions`}
                </span>
              </h2>
              {hadSpaces && (
                <p className="mb-2 text-sm text-muted-foreground">
                  Domains can’t contain spaces, so we swapped them for hyphens
                  (and a joined version).
                </p>
              )}
              <ul className="divide-y divide-border rounded-lg border">
                {results.map((result) => (
                  <ResultRow
                    key={result.name}
                    result={result}
                    picked={result.name === picked}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* ---------- Creative (AI) mode ---------- */}
      {isCreative && (
        <>
          {!hasTerm && !suggestLoading && (
            <div className="rounded-lg border bg-card p-4 text-muted-foreground shadow-xs">
              Describe your idea above — our AI suggests available, brandable
              names. Click one to check it across extensions.
            </div>
          )}

          {(suggestLoading || suggestions.length > 0) && (
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                Creative ideas
                {suggestLoading && (
                  <span role="status">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <span className="sr-only">Loading…</span>
                  </span>
                )}
              </h2>
              <p className="mb-2 text-sm text-muted-foreground">
                AI-generated brandable names — available, cheapest first. Click
                one to check it across extensions.
              </p>
              {suggestions.length > 0 && (
                <ul className="divide-y divide-border rounded-lg border">
                  {suggestions.map((result) => (
                    <ResultRow
                      key={`suggestion-${result.name}`}
                      result={result}
                      onPick={handlePick}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DomainSearch;
